/**
 * Soft-editorial accent palette.
 *
 * Each semantic role maps to a pastel "wash" (used for tinted backgrounds and
 * borderLeft accents) and a deeper "rule" colour (used for the 3-4px left rule,
 * borders, and dot indicators). Light + dark variants are paired so accents
 * remain readable on both surfaces.
 *
 * Brand alignment (strikeUp Brand Style Guide v1.0):
 *   info     → Pastel Blue   #DDECF0  (brand canonical)
 *   warning  → Pastel Yellow #F5EFDA  (brand canonical)
 *   healthy  → Pastel Green  #E8E8DF  (brand canonical)
 *   neutral  → Pastel Purple #EBEEFA  (brand canonical)
 *   risk     → desaturated rose  (EXTENDED — no danger pastel exists in the
 *              brand guide; semantic necessity for at-risk / error UI)
 *
 * Rule colours are derived darker partners of each wash (not in the brand
 * guide; tuned for AA contrast against the wash).
 *
 * Use roles to express section meaning across the app:
 *   healthy → success states, "all good" sections
 *   risk    → at-risk customers, error states, declining metrics
 *   warning → security violations, near-threshold KPIs
 *   info    → background jobs, neutral notifications
 *   neutral → ambient sectioning without semantic charge
 */

import { brand } from "./colors";

export interface AccentTone {
  /** Tinted background used for cards, info-boxes, toast surfaces. */
  wash: string;
  /** Stronger colour used for the 4px left rule and accent borders. */
  rule: string;
  /** Foreground text colour for use over the wash. */
  fg: string;
}

export interface AccentTokens {
  healthy: AccentTone;
  risk: AccentTone;
  warning: AccentTone;
  info: AccentTone;
  neutral: AccentTone;
}

export const accentsLight: AccentTokens = {
  healthy: { wash: brand.pastelGreen, rule: "#9DB89A", fg: "#3F5A40" },
  risk: { wash: "#F5DDD9", rule: "#C7857B", fg: "#7A3A33" },
  warning: { wash: brand.pastelYellow, rule: "#C9A85B", fg: "#6E5A1F" },
  info: { wash: brand.pastelBlue, rule: "#7FB3C2", fg: "#34616E" },
  neutral: { wash: brand.pastelPurple, rule: "#9396C2", fg: "#3D406B" },
};

export const accentsDark: AccentTokens = {
  healthy: { wash: "#1F2D22", rule: "#7FA77E", fg: "#C8DCC6" },
  risk: { wash: "#3A2421", rule: "#D89289", fg: "#F2D1CB" },
  warning: { wash: "#332A1A", rule: "#D9B767", fg: "#EFD9A2" },
  info: { wash: "#1F2D33", rule: "#8FBCC9", fg: "#CCE3EA" },
  neutral: { wash: "#26273A", rule: "#A3A6CE", fg: "#D4D6EE" },
};

export type AccentRole = keyof AccentTokens;
